import { Prisma } from '@prisma/client';
import _ from 'lodash';
import { asNumber } from '../../utils/numbers';
import { LotWeightAttributes, PalletTagWeightAttributes, default as ProductWeight } from '../product/weight';
import {
  CalculatedLineItemMetrics,
  LineItemExternalProfitInput,
  LineItemForMetrics,
  LineItemLandedCostInput,
  LineItemPricingBasis,
} from './types';

export default class LineItemMetrics {
  static getLineItemLandedCost(lineItem: LineItemLandedCostInput): number | null {
    const byWeight = lineItem.parentLineItem?.enteredByWeight ?? lineItem.enteredByWeight;

    const lotLandedCost = lineItem.lot?.metrics?.landedCostTotal ?? null;

    if (byWeight) {
      // We want the per-lb landed cost.
      if (lineItem.lot?.initialWeight && lotLandedCost) {
        return lotLandedCost / lineItem.lot.initialWeight;
      }

      // Fallback: calculate total weight from initialQuantity * weightPerInventoryUnit
      const wpiu = asNumber(lineItem.product?.weightPerInventoryUnit);
      const initialQuantity = lineItem.lot?.initialQuantity;
      if (wpiu && initialQuantity && lotLandedCost) {
        const totalWeight = initialQuantity * wpiu;
        return lotLandedCost / totalWeight;
      }
    } else {
      if (lineItem.lot?.initialQuantity && lotLandedCost) {
        return lotLandedCost / lineItem.lot.initialQuantity;
      }
    }

    return null;
  }

  static shouldUseEnteredWeightForPhysicalTotals(
    li: LineItemForMetrics,
    effectiveEnteredByWeight?: boolean | null
  ): boolean {
    const product = (li.product ?? li.orderedProduct) as { variableWeight?: boolean | null } | null | undefined;
    const enteredByWeight = effectiveEnteredByWeight ?? li.parentLineItem?.enteredByWeight ?? li.enteredByWeight;
    return Boolean(enteredByWeight) || Boolean(li.external && product?.variableWeight);
  }

  static isPricedByWeight(li: LineItemPricingBasis): boolean {
    return Boolean(li.parentLineItem?.enteredByWeight ?? li.enteredByWeight);
  }

  static calculateTotalPallets(lineItems: LineItemForMetrics[]): CalculatedLineItemMetrics {
    const numPallets = { ordered: 0, received: 0, picked: 0, shipped: 0, invoiced: 0 };

    lineItems.forEach((li) => {
      numPallets.ordered += LineItemMetrics.getLineItemPallets(li, 'ordered');
      numPallets.received += LineItemMetrics.getLineItemPallets(li, 'received');
      numPallets.picked += LineItemMetrics.getLineItemPallets(li, 'picked');
      numPallets.shipped += LineItemMetrics.getLineItemPallets(li, 'shipped');
      numPallets.invoiced += LineItemMetrics.getLineItemPallets(li, 'invoiced');
    });

    return { ...numPallets, picked: numPallets.picked + numPallets.shipped };
  }

  static getLineItemPallets(
    li: LineItemForMetrics,
    type: 'ordered' | 'received' | 'picked' | 'shipped' | 'invoiced'
  ): number {
    if (!li.product) {
      return 0;
    }

    const cartonsPerPallet = asNumber(li.product.cartonsPerPallet);

    // Pallet Tags only matter for picked line items, and only as a fallback.
    if (type === 'picked') {
      const populatedPalletTags = li.palletTagsOnLineItem?.filter((tag: any) => !!tag.quantity || !!tag.weight);
      if (populatedPalletTags?.length > 0 && (!cartonsPerPallet || li.product.variableWeight)) {
        return populatedPalletTags.length;
      }
    }

    if (!cartonsPerPallet) {
      return 0;
    }

    // For non-picked/shipped values, or if no pallet tags are available, use line item units
    if (type === 'ordered') {
      return (li.unitsOrdered ?? 0) / cartonsPerPallet;
    }

    if (type === 'received') {
      return (li.unitsReceived ?? 0) / cartonsPerPallet;
    }

    if (type === 'picked') {
      return (li.unitsPicked ?? 0) / cartonsPerPallet;
    }

    if (type === 'shipped') {
      return (li.unitsShipped ?? 0) / cartonsPerPallet;
    }

    if (type === 'invoiced') {
      return (li.unitsInvoiced ?? 0) / cartonsPerPallet;
    }

    return 0;
  }

  /**
   * Calculates the picked weight of a line item. Uses pallet tags if available, and handles entered by weight.
   */
  static getLineItemWeight(
    li: LineItemForMetrics,
    type: 'ordered' | 'received' | 'picked' | 'shipped' | 'invoiced'
  ): number {
    if (!li.product) {
      return 0;
    }

    const weightPerInventoryUnit = asNumber(li.product.weightPerInventoryUnit);

    // Pallet Tags take precedence for picked line items
    if (type === 'picked') {
      if (li.palletTagsOnLineItem?.length) {
        if (li.palletTagsOnLineItem.some((tag: any) => !!tag.weight)) {
          return li.palletTagsOnLineItem.reduce((acc: any, tag: any) => acc + (tag.weight ?? 0), 0);
        }

        const units = li.palletTagsOnLineItem.reduce((acc: any, tag: any) => acc + (tag.quantity ?? 0), 0);
        if (units && li.product.weightPerInventoryUnit) {
          return units * weightPerInventoryUnit;
        }
      }
    }

    // For non-picked/shipped weights, or if no pallet tags are available, use line item direct weight inputs if available, otherwise calculate from units
    if (type === 'ordered') {
      return li.weightOrdered ?? (li.unitsOrdered ? li.unitsOrdered * weightPerInventoryUnit : 0);
    }

    if (type === 'received') {
      return li.weightReceived ?? (li.unitsReceived ? li.unitsReceived * weightPerInventoryUnit : 0);
    }

    if (type === 'picked') {
      return li.weightPicked ?? (li.unitsPicked ? li.unitsPicked * weightPerInventoryUnit : 0);
    }

    if (type === 'shipped') {
      return li.weightShipped ?? (li.unitsShipped ? li.unitsShipped * weightPerInventoryUnit : 0);
    }

    if (type === 'invoiced') {
      return li.unitsInvoiced ? li.unitsInvoiced * weightPerInventoryUnit : 0;
    }

    return 0;
  }

  static calculateTotalUnits(lineItems: LineItemForMetrics[]): CalculatedLineItemMetrics {
    const MaterialInventoryProductIDs = {
      // Millennium Pacific
      4284: 'Pallet CHEP',
    } as const;
    const numUnits = { ordered: 0, received: 0, picked: 0, shipped: 0, invoiced: 0 };

    const nonMaterialInventoryLineItems = lineItems.filter(
      ({ productId }) => productId && !(productId in MaterialInventoryProductIDs)
    );

    nonMaterialInventoryLineItems?.forEach(
      ({ unitsOrdered, unitsReceived, unitsPicked, unitsShipped, unitsInvoiced }) => {
        numUnits.ordered += unitsOrdered ?? 0;
        numUnits.received += unitsReceived ?? 0;
        // external line items will be under "unitsShipped" as they are not picked from inventory
        numUnits.picked += unitsPicked || unitsShipped || 0;
        numUnits.shipped += unitsShipped ?? 0;
        numUnits.invoiced += unitsInvoiced ?? 0;
      }
    );

    return numUnits;
  }

  static calculateTotalWeight(lineItems: LineItemForMetrics[]): CalculatedLineItemMetrics {
    const sampleLineItem = lineItems?.[0];
    const product = sampleLineItem?.product || sampleLineItem?.orderedProduct;

    if (!sampleLineItem) {
      return { ordered: 0, received: 0, picked: 0, shipped: 0, invoiced: 0 };
    }

    if (typeof product?.weightPerInventoryUnit === 'number') {
      // Use regular JS numbers for weight calculations
      const totalWeight = { ordered: 0, received: 0, picked: 0, shipped: 0, invoiced: 0 };

      lineItems
        .filter((li) => li.product)
        .forEach((li) => {
          if (LineItemMetrics.shouldUseEnteredWeightForPhysicalTotals(li)) {
            totalWeight.ordered += li.weightOrdered || 0;
            totalWeight.received += li.weightReceived || 0;
            totalWeight.picked += li.weightPicked || 0;
            totalWeight.shipped += li.weightShipped || 0;
            return;
          }

          const productWeight = ProductWeight.of(li.product || li.orderedProduct)
            .withLot(li.lot as LotWeightAttributes)
            .withPalletTags(li.palletTagsOnLineItem?.map((pt) => pt.palletTag as PalletTagWeightAttributes) ?? [])
            .forQuantity(1);

          totalWeight.ordered = totalWeight.ordered + productWeight * (li.unitsOrdered ?? 0);
          totalWeight.received = totalWeight.received + productWeight * (li.unitsReceived ?? 0);
          totalWeight.picked = totalWeight.picked + productWeight * (li.unitsPicked ?? 0);
          totalWeight.shipped = totalWeight.shipped + productWeight * (li.unitsShipped ?? 0);
          totalWeight.invoiced = totalWeight.invoiced + productWeight * (li.unitsInvoiced ?? 0);
        });

      return {
        ordered: totalWeight.ordered,
        received: totalWeight.received,
        // external line items will be under shipped, so we need to include them with the picked metrics
        picked: totalWeight.picked + totalWeight.shipped,
        shipped: totalWeight.shipped,
        invoiced: totalWeight.invoiced,
      };
    }

    const totalWeight = {
      ordered: new Prisma.Decimal(0),
      received: new Prisma.Decimal(0),
      picked: new Prisma.Decimal(0),
      shipped: new Prisma.Decimal(0),
      invoiced: new Prisma.Decimal(0),
    };

    const lineItemById = _.keyBy(lineItems, 'id');

    lineItems
      .filter((li) => li.product)
      .forEach((li) => {
        const parent = li.parentLineItemId ? lineItemById[li.parentLineItemId] : null;

        // The entered-weight predicate covers two CHILDLESS top-level shapes: raw enteredByWeight parents (weightPicked is NULL — children own it) and external variable-weight lines (always childless, so !parent holds). Grouped parents carry the aggregated child weight in weightPicked.
        if (LineItemMetrics.shouldUseEnteredWeightForPhysicalTotals(li) && !parent) {
          totalWeight.ordered = totalWeight.ordered.add(li.weightOrdered || 0);
          totalWeight.received = totalWeight.received.add(li.weightReceived || 0);
          totalWeight.picked = totalWeight.picked.add(li.weightPicked || 0);
          totalWeight.shipped = totalWeight.shipped.add(li.weightShipped || 0);
          return;
        }

        // picked should only be updated by the child line item for entered by weight.
        if (parent?.enteredByWeight) {
          totalWeight.picked = totalWeight.picked.add(li.weightPicked || 0);
          return;
        }

        const productWeight = ProductWeight.of(li.product || li.orderedProduct)
          .withLot(li.lot as LotWeightAttributes)
          .withPalletTags(li.palletTagsOnLineItem?.map((pt) => pt.palletTag as PalletTagWeightAttributes) ?? [])
          .forQuantity(1);

        totalWeight.ordered = totalWeight.ordered.add(productWeight * (li.unitsOrdered ?? 0));
        totalWeight.received = totalWeight.received.add(productWeight * (li.unitsReceived ?? 0));
        totalWeight.picked = totalWeight.picked.add(productWeight * (li.unitsPicked ?? 0));
        totalWeight.shipped = totalWeight.shipped.add(productWeight * (li.unitsShipped ?? 0));
        totalWeight.invoiced = totalWeight.invoiced.add(productWeight * (li.unitsInvoiced ?? 0));
      });

    return {
      // We force weights to 0 decimal places, rounding any weights to the nearest pound
      ordered: totalWeight.ordered.toNumber(),
      received: totalWeight.received.toNumber(),
      // external line items will be under shipped, so we need to include them with the picked metrics
      picked: totalWeight.picked.add(totalWeight.shipped).toNumber(),
      shipped: totalWeight.shipped.toNumber(),
      invoiced: totalWeight.invoiced.toNumber(),
    };
  }

  static lineItemPrice(li: LineItemExternalProfitInput, fobOnly = false): number {
    // TAS-3168: read USD-normalized sellPrice / deliveredPrice columns only.
    // sellCurrencyPrice / deliveredCurrencyPrice hold the foreign-currency value
    // (e.g. CAD) and must NOT be mixed with USD COGS in metrics math.
    const fob = li.parentLineItem?.sellPrice ?? li.sellPrice ?? 0;

    if (fobOnly) {
      return fob;
    }

    const dlv = li.parentLineItem?.deliveredPrice ?? li.deliveredPrice;

    // deliveredPrice is nullable w/ no default (orders.prisma:709,822) and may hold a stale 0
    // pre-recompute; use || so a 0 falls back to FOB (delivered >= FOB, so a real $0 dlv implies $0 fob).
    return dlv || fob;
  }

  static calculateExternalLineItemProfit(lineItem: LineItemExternalProfitInput, quantity: number): number {
    // TAS-3168: USD-only arithmetic. buyPrice is USD-normalized at write time.
    // TAS-3886: line profit = (FOB − landed cost) × quantity; the DLV price carries freight, not margin.
    const sellPrice =
      lineItem.parentLineItem?.sellPrice ?? lineItem.sellPrice ?? LineItemMetrics.lineItemPrice(lineItem);
    const buyPrice = lineItem.buyPrice ?? 0;
    return (sellPrice - buyPrice - LineItemMetrics.poChargePerPricingUnit(lineItem)) * (quantity ?? 0);
  }

  /** `directLandedCost` adds the PO's AP charges per carton; a by-weight line prices per lb. */
  private static poChargePerPricingUnit(lineItem: LineItemExternalProfitInput): number {
    if (lineItem.directLandedCost == null) return 0;

    const perUnit = lineItem.directLandedCost - (lineItem.buyPrice ?? 0);
    if (!LineItemMetrics.isPricedByWeight(lineItem)) return perUnit;

    const units = lineItem.unitsShipped ?? lineItem.unitsOrdered ?? 0;
    const weight = lineItem.weightShipped ?? lineItem.weightOrdered ?? 0;
    return weight ? (perUnit * units) / weight : 0;
  }
}
